import React from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  CheckCircle,
  XCircle,
  Loader2,
  User,
  Mail,
  Shield,
} from "lucide-react";

const StatusChangeModal = ({
  isOpen,
  admin,
  newStatus,
  onClose,
  onConfirm,
  loading,
}) => {
  if (!isOpen || !admin) return null;

  const isApproving = newStatus === true;
  const actionLabel = isApproving ? "Approve" : "Disapprove";
  const actionDescription = isApproving
    ? "This administrator will be able to access the system."
    : "This administrator will no longer be able to access the system.";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className={`flex flex-col items-center gap-3 p-6 text-center ${
              isApproving
                ? "bg-gradient-to-br from-emerald-50 to-green-50"
                : "bg-gradient-to-br from-red-50 to-orange-50"
            }`}
          >
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-full shadow-lg ${
                isApproving
                  ? "bg-gradient-to-br from-emerald-500 to-green-600"
                  : "bg-gradient-to-br from-red-500 to-orange-500"
              }`}
            >
              {isApproving ? (
                <CheckCircle className="h-6 w-6 text-white" />
              ) : (
                <XCircle className="h-6 w-6 text-white" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                {actionLabel} Administrator
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                {actionDescription}
              </p>
            </div>
          </div>

          {/* Body */}
          <div className="p-4">
            <p className="mb-3 text-center text-sm text-gray-700">
              Are you sure you want to {actionLabel.toLowerCase()}{" "}
              <span
                className={`font-semibold ${
                  isApproving ? "text-emerald-700" : "text-red-700"
                }`}
              >
                {admin.first_name} {admin.last_name}
              </span>
              ?
            </p>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
              <div className="flex gap-3 items-start">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
                  <User className="h-4 w-4 text-gray-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {admin.first_name} {admin.last_name}
                  </p>
                  <div className="mt-1 space-y-0.5 text-xs text-gray-600">
                    <div className="flex items-center gap-1.5">
                      <Mail className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{admin.email}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Shield className="h-3 w-3 flex-shrink-0" />
                      <span>{admin.role || "Administrator"}</span>
                    </div>
                  </div>
                  <span
                    className={`mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                      admin.status === true ||
                      admin.status === 1 ||
                      admin.status === "1" ||
                      admin.status === "active"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {admin.status === true ||
                    admin.status === 1 ||
                    admin.status === "1" ||
                    admin.status === "active"
                      ? "Active"
                      : "Inactive"}{" "}
                    → {isApproving ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 border-t border-gray-100 bg-gray-50/80 p-4">
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              disabled={loading}
              className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </motion.button>
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onConfirm(admin, newStatus)}
              disabled={loading}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition disabled:opacity-50 ${
                isApproving
                  ? "bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700"
                  : "bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  {isApproving ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <XCircle className="h-4 w-4" />
                  )}
                  {actionLabel}
                </>
              )}
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default StatusChangeModal;
